import { Injectable } from "@angular/core";
import {MessageService} from 'primeng/api';

@Injectable()
/**
 * @whatItDoes
 *
 * Handles HTTP errors in the aplication and provides a UI hook for displaying errors to the user.
 *
 * @description
 *
 * The default global error handler logs errors to the console. This class is designed to staticially
 *      handle HTTP errors app-wide. An instance of the class is instantiated and injected with the
 *      MessageService dependency at the root level of the aplication. Any service can statically call
 *      the `handleError()` method on http gets to pipe errors to the console, ro the user, or use
 *      any custom error logic.
 *
 * ### Example
 *
 * ```
	public LoadQuoteFromWebService(): Observable<Quote> {
		let objResult: Observable<Quote>;
		let strUrl: string;

		strUrl = environment.WebServiceUrl + QuotingService.QuoteControllerPath + "GetQuote/";
		objResult = this._HttpClient.get<Quote>(strUrl)
            .do()
            .catch(err => HttpErrorHandler.handleError(err, alertUser?=true, log?=true));
    }
 * ```
 *
 * @stable
 */
export class ErrorHandler {

	/** A summary of the error for user consumption. */
	private static readonly USER_FACING_ERROR_MESSAGE_SUMMARY: string = "An Error Occurred!";

	/** The detail of the error message for user consumption. */
	private static readonly USER_FACING_ERROR_MESSAGE_DETAIL: string = "Please try your request again.";

	/** The default message lifespan in ms. */
	private static readonly DEFAULT_MESSAGE_LIFESPAN_MILLISECONDS: number = 5000;

	/** Holds the last error message encountered by the client. */
	public LastErrorMessage: string;

	/**
     * Constructor.
     *
     * @param _MessageService The globally injected message service from PrimeNG for the toast.
     */
	constructor(
		private _MessageService: MessageService
	) {}

	/**
	 * Alerts the user to an error. Implements the message service.
	 * Note: please leave technical detail out of user-facing errors.
	 *
	 * @param Summary A summary of the error. The marquee on the toast object.
	 * @param DetailedMessage The error detail. For a toast, the smaller writing on the object.
	 */
	public ShowError(Summary: string, DetailedMessage: string): void {
		let strSummary: string, strDetail: string;

		if (Summary == null)
			strSummary = ErrorHandler.USER_FACING_ERROR_MESSAGE_SUMMARY;
		else
			strSummary = Summary;
		if (DetailedMessage == null)
			strDetail = ErrorHandler.USER_FACING_ERROR_MESSAGE_DETAIL;
		else
			strDetail = DetailedMessage;
		this.LastErrorMessage = strDetail;

		this._MessageService.add({severity: 'error', summary: strSummary, detail: strDetail, life: ErrorHandler.DEFAULT_MESSAGE_LIFESPAN_MILLISECONDS});
	}

	/**
	 * Alerts the user to an info message. Implements the message service.
	 * Note: please leave technical detail out of user-facing info messages.
	 *
	 * @param Summary A summary of the info message. The marquee on the toast object.
	 * @param DetailedMessage The info message detail. For a toast, the smaller writing on the object.
	 */
	 public ShowInfo(Summary: string, DetailedMessage: string): void {
		let strSummary: string, strDetail: string;

		if (!Summary && !DetailedMessage) {
			return;
		}

		strSummary = Summary;
		strDetail = DetailedMessage;

		this._MessageService.add({severity: 'info', summary: strSummary, detail: strDetail, life: ErrorHandler.DEFAULT_MESSAGE_LIFESPAN_MILLISECONDS});
	}
}